import { useQuery } from "@tanstack/react-query";
import { getCatalog, listVersions } from "./catalog";
import { config } from "@/config";
import { SESSION_NOT_FOUND_TEXT } from "@/lib/labels";
import { getSession, listOpenSessions, openDrafts } from "./workflow";
import { queryKeys } from "./queryKeys";
import { listWorkspaces } from "./tenancy";

/** Reads used by more than one screen. Each is one key and one fetcher, so the screens share a single request. */
export const useWorkspaces = (tenantId: string | null) =>
  useQuery({ queryKey: queryKeys.workspaces(tenantId), queryFn: () => listWorkspaces(tenantId!), enabled: !!tenantId });

export const useCatalog = (workspaceId: string | null) =>
  useQuery({ queryKey: queryKeys.catalog(workspaceId), queryFn: () => getCatalog(workspaceId!), enabled: !!workspaceId });

export const useVersions = (workspaceId: string | null) =>
  useQuery({ queryKey: queryKeys.versions(workspaceId), queryFn: () => listVersions(workspaceId!), enabled: !!workspaceId });

export const useOpenSessions = (workspaceId: string | null) =>
  useQuery({ queryKey: queryKeys.openSessions(workspaceId), queryFn: () => listOpenSessions(workspaceId!), enabled: !!workspaceId });

/** The session's row is gone (such as deleted). Reading it again cannot bring it back. */
export class SessionNotFoundError extends Error {
  constructor() {
    super(SESSION_NOT_FOUND_TEXT);
    this.name = "SessionNotFoundError";
  }
}

/** The app's retry rule for every read: a session that is not found fails at once, any other failure (a network error) is tried again. */
export const retryRead = (failureCount: number, error: Error) => !(error instanceof SessionNotFoundError) && failureCount < config.readRetries;

/** The active session's full row, with its `state`. A missing row is an error, never "no session", so a screen can offer Try again. */
export const useOpenSession = (sessionId: string | null | undefined) =>
  useQuery({
    queryKey: queryKeys.openSession(sessionId),
    queryFn: async () => {
      const session = await getSession(sessionId!);
      if (!session) throw new SessionNotFoundError();
      return session;
    },
    enabled: !!sessionId,
  });

export const useOpenDrafts = (workspaceId: string | null) =>
  useQuery({ queryKey: queryKeys.openDrafts(workspaceId), queryFn: () => openDrafts(workspaceId!), enabled: !!workspaceId });

/** The latest read failed. Older data may still be held, but it may be out of date, so it is not shown. */
export const loadFailed = (q: { isError: boolean }) => q.isError;

/** The data a page may show. After a failed refetch TanStack still holds the old data, so a failed read shows none. */
export const shownData = <T>(q: { isError: boolean; data: T | undefined }) => (q.isError ? undefined : q.data);

/** A retry that reads again only the queries that failed. */
export const retryFailed = (...queries: Array<{ isError: boolean; refetch: () => unknown }>) => () => {
  for (const q of queries) if (q.isError) void q.refetch();
};

/**
 * What a page shows for a read that needs the tenant or workspace. A read with no workspace yet is disabled and stays
 * pending for ever, so the shell says why: still resolving (loading), could not be read (failed), or there is none (empty).
 */
export function readStatus(
  q: { isPending: boolean; isError: boolean; fetchStatus: "idle" | "fetching" | "paused"; refetch: () => unknown },
  shell: { activeSessionLoading: boolean; activeSessionError: boolean; reloadActiveSession: () => void },
) {
  const disabled = q.isPending && q.fetchStatus === "idle";
  return {
    loading: q.isPending && (disabled ? shell.activeSessionLoading : true),
    failed: loadFailed(q) || (disabled && shell.activeSessionError),
    retry: () => { shell.reloadActiveSession(); retryFailed(q)(); },
  };
}
